// WHEN A SET IS DEALT, as opposed to when its prices were read.
//
// The pad has always cut the board at `snapshot.fetchedAt` — the moment the
// prices were fetched. That is the right cutoff for the board the pad opened
// on, and the wrong one for a set dealt hours later: at 3pm on a 9am board it
// deals legs whose match kicked off at noon, and a bookmaker will not take a
// multi with a leg already under way without a phone call.
//
// So a deal has a cutoff of its own, and it is a MOMENT SOMEBODY ACTED, never
// the minute clock. The clock ticking must not make tickets vanish off the live
// pad as matches kick off — a printed ticket that has since started is the
// honest state, and Ticket.vue already stamps "started" beside the leg. See
// `dealCutoff` in store.js for the three events that move it.
//
// Pure, and a leaf: no Vue, no store, no clock of its own.

/**
 * An ISO stamp to the second. A cutoff is a moment somebody acted, not a
 * millisecond, and the milliseconds would only ever be noise in a stamp that
 * gets printed and compared.
 */
export function isoSeconds(ms) {
  return new Date(ms).toISOString().replace(/\.\d{3}Z$/, 'Z');
}

/**
 * The moment to cut a deal at: what goes in `universe.asOf` and `ctx.asOf`, and
 * therefore what `buildUniverse` and `buildPool` both drop a match for starting
 * before.
 *
 * With `allowStarted` the prices' own time stands, which is the pad exactly as
 * it has always run — for the punter who is happy to ring a bet through.
 * Without it, the later of the fetch and the deal cutoff: a set dealt at 3pm
 * cannot carry a match that started at noon, however old the board is.
 *
 * `fetchedAt` is handed back VERBATIM whenever it is the later of the two — the
 * ordinary case, a board just fetched — so a fresh pad is byte-for-byte what it
 * was before this existed, down to the milliseconds on the ticket's stamp.
 *
 * Anything unreadable falls back to `fetchedAt`: a cutoff nobody can parse is
 * not a reason to start dropping matches.
 */
export function dealAsOf({ fetchedAt, dealCutoff, allowStarted = false } = {}) {
  if (allowStarted) return fetchedAt;
  const cutoff = Number(dealCutoff);
  if (!Number.isFinite(cutoff)) return fetchedAt;
  const fetched = new Date(fetchedAt).getTime();
  if (Number.isFinite(fetched) && fetched >= cutoff) return fetchedAt;
  return isoSeconds(cutoff);
}
